import React, { useState, useEffect } from "react";
import { BiChevronRight, BiSearch } from "react-icons/bi";
import { FaEye, FaChevronDown } from "react-icons/fa6";
import { IoChevronBack } from "react-icons/io5";
import { Order, LogisticsOrderItem } from "../../types/orders";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getLogisticsOrders } from "../../redux/thunk";
import toast from "react-hot-toast";

// Reusable Modal Components
const OrderDetailsModal: React.FC<{
  order: Order;
  isOpen: boolean;
  onClose: () => void;
}> = ({ order, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-emerald-600">
              Order Details
            </h3>
            <p className="text-sm text-gray-500">
              View detailed information about order {order.orderNumber}{" "}
              including delivery route, rider assignment, and status.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-lg font-medium">Order {order.orderNumber}</h4>
              <p className="text-sm text-gray-600">{order.customer.name}</p>
            </div>
            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
              Assigned
            </span>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <h5 className="font-medium mb-3">Customer Information:</h5>
              <div className="space-y-2">
                <p className="text-sm flex items-center">
                  <span className="w-4 h-4 mr-2">👤</span>
                  {order.customer.name}
                </p>
                <p className="text-sm flex items-center">
                  <span className="w-4 h-4 mr-2">📞</span>
                  {order.customer.phone}
                </p>
                <p className="text-sm flex items-center">
                  <span className="w-4 h-4 mr-2">📍</span>
                  {order.customer.address}
                </p>
              </div>
            </div>

            <div>
              <h5 className="font-medium mb-3">Restaurant Information:</h5>
              <div className="space-y-2">
                <p className="text-sm flex items-center">
                  <span className="w-4 h-4 mr-2">🏪</span>
                  {order.restaurant.name}
                </p>
                <p className="text-sm flex items-center">
                  <span className="w-4 h-4 mr-2">📞</span>
                  {order.restaurant.phone}
                </p>
                <p className="text-sm flex items-center">
                  <span className="w-4 h-4 mr-2">📍</span>
                  {order.restaurant.address}
                </p>
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <h5 className="font-medium">Delivery Route:</h5>
              <p className="text-sm text-gray-600">
                Assigned Rider: {order.rider?.name}
              </p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                    <span className="font-medium">Pick-up Location</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {order.deliveryRoute.pickupLocation.address}
                  </p>
                </div>

                <div className="flex-shrink-0">
                  <div className="w-8 h-0.5 bg-gray-300"></div>
                </div>

                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                    <span className="font-medium">Delivery Location</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {order.deliveryRoute.deliveryLocation.address}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h5 className="font-medium mb-3">Payment Type:</h5>
            <span className="text-emerald-600 font-medium">
              Pay on Delivery
            </span>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ReassignOrderModal: React.FC<{
  order: Order;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (riderId: string, reason: string) => void;
}> = ({ order, isOpen, onClose, onConfirm }) => {
  const [selectedRider, setSelectedRider] = useState("");
  const [reason, setReason] = useState("");

  // Mock available riders data
  const availableRiders = [
    {
      id: "1",
      name: "Emanuel Wilson",
      area: "Gbagada Area, Mainland",
      distance: "1.5km",
      orders: 123,
      rating: 4.5,
      type: "Internal",
    },
    {
      id: "2",
      name: "Emanuel Wilson",
      area: "Gbagada Area, Mainland",
      distance: "1.5km",
      orders: 123,
      rating: 4.5,
      type: "External",
    },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-emerald-600">
              Reassign Order
            </h3>
            <p className="text-sm text-gray-500">
              Reassign order {order.orderNumber} to a different rider. Current
              rider: {order.rider?.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-lg font-medium">Order {order.orderNumber}</h4>
              <p className="text-sm text-gray-600">{order.customer.name}</p>
            </div>
            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
              En-route
            </span>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <h5 className="font-medium mb-3">Customer Information:</h5>
              <div className="space-y-2">
                <p className="text-sm flex items-center">
                  <span className="w-4 h-4 mr-2">👤</span>
                  {order.customer.name}
                </p>
                <p className="text-sm flex items-center">
                  <span className="w-4 h-4 mr-2">📞</span>
                  {order.customer.phone}
                </p>
                <p className="text-sm flex items-center">
                  <span className="w-4 h-4 mr-2">📍</span>
                  {order.customer.address}
                </p>
              </div>
            </div>

            <div>
              <h5 className="font-medium mb-3">Restaurant Information:</h5>
              <div className="space-y-2">
                <p className="text-sm flex items-center">
                  <span className="w-4 h-4 mr-2">🏪</span>
                  {order.restaurant.name}
                </p>
                <p className="text-sm flex items-center">
                  <span className="w-4 h-4 mr-2">📞</span>
                  {order.restaurant.phone}
                </p>
                <p className="text-sm flex items-center">
                  <span className="w-4 h-4 mr-2">📍</span>
                  {order.restaurant.address}
                </p>
              </div>
            </div>
          </div>

          <div>
            <h5 className="font-medium mb-3">
              Select a New Rider ({availableRiders.length})
            </h5>
            <div className="space-y-3">
              {availableRiders.map((rider) => (
                <div
                  key={rider.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedRider === rider.id
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => setSelectedRider(rider.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="rider"
                        checked={selectedRider === rider.id}
                        onChange={() => setSelectedRider(rider.id)}
                        className="w-4 h-4 text-emerald-600 border-gray-300 focus:ring-emerald-500"
                      />
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">{rider.name}</span>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                            {rider.type}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{rider.area}</p>
                        <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                          <span>⭐ {rider.rating}</span>
                          <span>🚴 Bicycle</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">Distance to Pickup</p>
                      <p className="text-emerald-600">{rider.distance}</p>
                      <p className="text-xs text-gray-500">
                        {rider.orders} Orders
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h5 className="font-medium mb-3">Reason for reassignment</h5>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
              rows={3}
              placeholder="Enter the reason for reassigning this order....."
            />
          </div>
        </div>

        <div className="mt-6 flex justify-between">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel Reassignment
          </button>
          <button
            onClick={() => {
              if (selectedRider && reason) {
                onConfirm(selectedRider, reason);
                onClose();
              } else {
                toast.error("Please select a rider and provide a reason");
              }
            }}
            disabled={!selectedRider || !reason}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm Reassignment
          </button>
        </div>
      </div>
    </div>
  );
};

const OrderDispatch: React.FC = () => {
  const dispatch = useAppDispatch();
  const { orders, ordersPagination, ordersLoading, ordersError } = useAppSelector((state) => state.logistics);
  
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [sortFilter, setSortFilter] = useState("Most Recent");
  const [currentPage, setCurrentPage] = useState(1);
  
  // Fetch orders on component mount and when page changes
  useEffect(() => {
    dispatch(getLogisticsOrders({ page: currentPage, limit: 10 }));
  }, [dispatch, currentPage]);

  // Mock data structure for modals - this should be updated when backend provides order details
  const createMockOrder = (orderItem: LogisticsOrderItem): Order => ({
    id: orderItem.orderNumber,
    orderNumber: orderItem.orderNumber,
    customer: {
      id: "mock-customer-id",
      name: orderItem.orderNumber.split('-')[1] || "Customer Name",
      phone: "+234 567 890 123",
      address: "12 Avenue Close, Gbagdada",
    },
    restaurant: {
      id: "mock-restaurant-id",
      name: "Peteri Amadu",
      phone: "+234 567 890 123",
      address: "12 Avenue Close, Gbagdada",
    },
    rider: {
      id: "mock-rider-id",
      name: orderItem.riderName,
      phone: "+234 567 890 123",
      vehicleType: "bicycle",
      rating: 4.5,
    },
    items: [
      { id: "1", name: "Jollof Rice", quantity: 2, price: 1500 },
      { id: "2", name: "Pepsi Pet", quantity: 1, price: 400 },
    ],
    status: mapOrderStatus(orderItem.orderStatus),
    priority: "normal",
    total: 3400,
    deliveryRoute: {
      pickupLocation: {
        name: "Restaurant Location",
        address: "No 4 Estaport, Avenue, Gbagada",
      },
      deliveryLocation: {
        name: "Customer Location",
        address: "12 Avenue Close, Gbagdada",
      },
    },
    timeline: [
      {
        status: "pending",
        timestamp: "02:20pm",
        description: "Order received",
        completed: true,
      },
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  // Map API order status to component status
  const mapOrderStatus = (apiStatus: string): Order['status'] => {
    const statusMap: Record<string, Order['status']> = {
      'pending': 'pending',
      'pick up': 'picked_up',
      'accept pick up': 'assigned',
      'at delivery location': 'out_for_delivery',
      'delivered': 'delivered',
    };
    return statusMap[apiStatus.toLowerCase()] || 'pending';
  };

  // Convert LogisticsOrderItem to Order format for existing component logic
  const convertedOrders: Order[] = orders.map(createMockOrder);


  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case "assigned":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "picked_up":
        return "bg-blue-100 text-blue-800";
      case "delivered":
        return "bg-green-100 text-green-800";
      case "flagged":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case "assigned":
        return "Assigned";
      case "pending":
        return "Pending";
      case "picked_up":
        return "Pick Up";
      case "flagged":
        return "Flagged";
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  // Filter orders based on search and filters
  const filteredOrders = convertedOrders.filter((order) => {
    const matchesSearch =
      order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.rider?.name.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "All Statuses" ||
      order.status.toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const handleConfirmReassignment = (riderId: string, reason: string) => {
    toast.success("Order reassigned successfully");
    // Implement reassignment logic
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button className="text-gray-400 hover:text-gray-600 transition-colors">
              <IoChevronBack className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-sm text-gray-500">Logistics</span>
                <BiChevronRight className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-500">Order Dispatch</span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                Order Dispatch
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage rider applications, profiles, and status
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Error Display */}
        {ordersError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            <p>Error: {ordersError}</p>
          </div>
        )}
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-lg">
              <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search Riders"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>

            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option>All Statuses</option>
                <option>Assigned</option>
                <option>Pending</option>
                <option>Pick Up</option>
                <option>Delivered</option>
                <option>Flagged</option>
              </select>
              <FaChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 pointer-events-none" />
            </div>

            <div className="relative">
              <select
                value={sortFilter}
                onChange={(e) => setSortFilter(e.target.value)}
                className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option>Most Recent</option>
                <option>Oldest First</option>
                <option>Priority</option>
              </select>
              <FaChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left py-3 px-6 font-medium text-gray-500 text-sm">
                  Order Number/ Customer
                </th>
                <th className="text-left py-3 px-6 font-medium text-gray-500 text-sm">
                  Rider
                </th>
                <th className="text-left py-3 px-6 font-medium text-gray-500 text-sm">
                  Status
                </th>
                <th className="text-left py-3 px-6 font-medium text-gray-500 text-sm">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {ordersLoading ? (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-gray-500">
                    Loading orders...
                  </td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-gray-500">
                    No orders found
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="py-4 px-6">
                    <div className="font-medium text-gray-900">
                      {order.orderNumber}
                      {order.customer.name}
                    </div>
                  </td>
                  <td className="py-4 px-6 text-sm text-gray-900">
                    {order.rider?.name || "Unassigned"}
                  </td>
                  <td className="py-4 px-6">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(
                        order.status
                      )}`}
                    >
                      {getStatusText(order.status)}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <button
                      onClick={() => handleViewOrder(order)}
                      className="inline-flex items-center space-x-1 text-emerald-600 border border-emerald-600 px-3 py-1 rounded-lg text-sm hover:bg-emerald-50 transition-colors"
                    >
                      <span>View</span>
                      <FaEye className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="border-t border-gray-200 px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                {ordersPagination ? (
                  `Showing page ${ordersPagination.page} of ${ordersPagination.totalPages} (${ordersPagination.totalRiders} total riders)`
                ) : (
                  'Loading...'
                )}
              </div>
              <div className="flex items-center space-x-2">
                <button 
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1 || ordersLoading || !ordersPagination}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  &lt;
                </button>
                
                {/* Generate page numbers */}
                {ordersPagination && Array.from({ length: Math.min(5, ordersPagination.totalPages) }, (_, index) => {
                  const pageNumber = Math.max(1, Math.min(
                    ordersPagination.totalPages - 4,
                    Math.max(1, currentPage - 2)
                  )) + index;
                  
                  if (pageNumber <= ordersPagination.totalPages) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => setCurrentPage(pageNumber)}
                        disabled={ordersLoading}
                        className={`px-3 py-1 text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          currentPage === pageNumber
                            ? "bg-emerald-600 text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  }
                  return null;
                })}
                
                {ordersPagination && ordersPagination.totalPages > 5 && currentPage < ordersPagination.totalPages - 2 && (
                  <>
                    <span className="px-3 py-1 text-sm text-gray-600">...</span>
                    <button
                      onClick={() => setCurrentPage(ordersPagination.totalPages)}
                      disabled={ordersLoading}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {ordersPagination.totalPages}
                    </button>
                  </>
                )}
                
                <button 
                  onClick={() => setCurrentPage(Math.min(ordersPagination?.totalPages || 1, currentPage + 1))}
                  disabled={!ordersPagination || currentPage === ordersPagination.totalPages || ordersLoading}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  &gt;
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {selectedOrder && (
        <>
          <OrderDetailsModal
            order={selectedOrder}
            isOpen={showOrderModal}
            onClose={() => {
              setShowOrderModal(false);
              setSelectedOrder(null);
            }}
          />

          <ReassignOrderModal
            order={selectedOrder}
            isOpen={showReassignModal}
            onClose={() => {
              setShowReassignModal(false);
              setSelectedOrder(null);
            }}
            onConfirm={handleConfirmReassignment}
          />
        </>
      )}
    </div>
  );
};

export default OrderDispatch;
